import { useEffect, useRef } from 'react'

/**
 * Custom hook for dynamically changing favicon and browser tab title
 * @param {Object} options - Configuration options
 * @param {string} options.faviconPath - Path to the favicon file (relative to public folder)
 * @param {string} options.title - Browser tab title to set
 * @param {boolean} options.condition - Condition that determines when to apply the changes
 * @param {boolean} options.enabled - Whether the hook is enabled (default: true)
 * @param {string} options.faviconType - Type of favicon link to update (default: 'shortcut icon')
 */
export const useDynamicFavicon = ({
  faviconPath,
  title,
  condition,
  enabled = true,
  faviconType = 'shortcut icon'
}) => {
  const originalTitle = useRef(document.title)
  const originalFavicon = useRef(null)
  const linkElement = useRef(null)

  useEffect(() => {
    if (!enabled) return

    // Store original favicon on first run
    if (!originalFavicon.current) {
      const existingLink = document.querySelector(`link[rel="${faviconType}"]`)
      if (existingLink) {
        originalFavicon.current = existingLink.href
      }
    }

    if (condition) {
      // Change favicon
      if (faviconPath) {
        // Remove existing link if it exists
        if (linkElement.current) {
          document.head.removeChild(linkElement.current)
        }

        // Create new link element
        linkElement.current = document.createElement('link')
        linkElement.current.rel = faviconType
        linkElement.current.href = faviconPath
        linkElement.current.type = 'image/x-icon'
        document.head.appendChild(linkElement.current)
      }

      // Change title
      if (title) {
        document.title = title
      }
    } else {
      // Restore original favicon
      if (linkElement.current) {
        document.head.removeChild(linkElement.current)
        linkElement.current = null
      }

      if (originalFavicon.current) {
        const restoreLink = document.createElement('link')
        restoreLink.rel = faviconType
        restoreLink.href = originalFavicon.current
        restoreLink.type = 'image/x-icon'
        document.head.appendChild(restoreLink)
      }

      // Restore original title
      document.title = originalTitle.current
    }

    // Cleanup function
    return () => {
      if (linkElement.current) {
        document.head.removeChild(linkElement.current)
        linkElement.current = null
      }
      document.title = originalTitle.current
    }
  }, [condition, faviconPath, title, enabled, faviconType])
}
